import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Warning, Inventory2 } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth.js';
import { InventoryService } from '../../services/inventoryService.js';

export const InventoryPanel = () => {
  const { outletId } = useAuth();
  const [inventoryItems, setInventoryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadInventory = async () => {
      if (!outletId) return;
      setIsLoading(true);
      try {
        const data = await InventoryService.fetchTodayInventory(outletId);
        const items = data?.items || [];
        // Sort by remaining qty ascending → low-stock first
        const sorted = items
          .filter(item => item.quantity !== undefined)
          .sort((a, b) => {
            const remA = a.quantity - (a.used || 0);
            const remB = b.quantity - (b.used || 0);
            return remA - remB;
          });
        setInventoryItems(sorted);
      } catch (error) {
        console.error('Error loading inventory:', error);
        setInventoryItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadInventory();
  }, [outletId]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress size={24} sx={{ color: 'text.secondary' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, py: 1.5, overflow: 'auto', height: '100%' }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          fontSize: '0.65rem',
          mb: 1.5,
          display: 'block',
        }}
      >
        Needs Restocking
      </Typography>

      {inventoryItems.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            borderRadius: '14px',
            bgcolor: 'rgba(255, 255, 255, 0.5)',
          }}
        >
          <Inventory2 sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 500 }}>
            No inventory data
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5 }}>
            Items needing restock will appear here
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {inventoryItems.map((item, idx) => {
            const remaining = item.quantity - (item.used || 0);
            const isLow = remaining <= item.quantity * 0.2; // below 20% is low
            const isCritical = remaining <= item.quantity * 0.1; // below 10% is critical

            return (
              <Box
                key={item.id || idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  px: 1.25,
                  borderRadius: '10px',
                  bgcolor: isCritical
                    ? 'rgba(255, 59, 48, 0.06)'
                    : isLow
                      ? 'rgba(255, 149, 0, 0.06)'
                      : 'transparent',
                  transition: 'background-color 0.2s',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: '0.82rem' }}
                    noWrap
                  >
                    {item.item_name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                    {item.unit || 'units'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                  {(isLow || isCritical) && (
                    <Warning
                      sx={{
                        fontSize: 14,
                        color: isCritical ? '#FF3B30' : '#FF9500',
                      }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      color: isCritical
                        ? '#FF3B30'
                        : isLow
                          ? '#FF9500'
                          : '#1D1D1F',
                      minWidth: 28,
                      textAlign: 'right',
                    }}
                  >
                    {remaining}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
